import React, { useState, useEffect, useCallback } from 'react';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon,
  UserIcon,
  CalendarIcon,
  ChevronDownIcon,
  ChevronUpIcon
} from '@heroicons/react/24/outline';
import { noteApi } from '../services/noteApi';
import { Note } from '../types/Note';
import NotesForm from './NotesForm';

interface LeadNotesTabProps {
  leadId: string;
  leadName: string;
  leadEmail: string;
  leadPhone?: string;
}

const LeadNotesTab: React.FC<LeadNotesTabProps> = ({ 
  leadId, 
  leadName, 
  leadEmail, 
  leadPhone 
}) => {
  const [notes, setNotes] = useState<Note[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingNote, setEditingNote] = useState<Note | null>(null);
  const [expandedNotes, setExpandedNotes] = useState<Set<number>>(new Set());

  const fetchNotes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await noteApi.getNotesByLeadId(parseInt(leadId));
      
      if (response.success) {
        setNotes(response.data);
      } else {
        setError(response.message || 'Failed to fetch notes');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch notes');
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  const handleCreateNote = () => {
    setEditingNote(null);
    setShowForm(true);
  };

  const handleEditNote = (note: Note) => {
    setEditingNote(note);
    setShowForm(true);
  };

  const handleDeleteNote = async (noteId: number) => {
    if (!window.confirm('Are you sure you want to delete this note?')) {
      return;
    }

    try {
      const response = await noteApi.deleteNote(noteId);
      if (response.success) {
        await fetchNotes();
      } else {
        setError(response.message || 'Failed to delete note');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to delete note');
    }
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingNote(null);
    fetchNotes();
  };

  const toggleNoteExpansion = (noteId: number) => {
    setExpandedNotes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(noteId)) {
        newSet.delete(noteId);
      } else {
        newSet.add(noteId);
      }
      return newSet;
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const truncateContent = (content: string, maxLength: number = 150) => {
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength) + '...';
  };

  const isNoteExpanded = (noteId: number) => expandedNotes.has(noteId);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Add Button */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Notes</h3>
          <p className="text-sm text-gray-500">
            Notes and observations for {leadName}
          </p>
        </div>
        <button
          onClick={handleCreateNote}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Add Note
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Notes List */}
      {notes.length === 0 ? (
        <div className="text-center py-12">
          <div className="mx-auto h-24 w-24 text-gray-400 mb-4">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Notes Yet</h3>
          <p className="text-gray-500 mb-4">Get started by creating your first note for this lead.</p>
          <button
            onClick={handleCreateNote}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Add Note
          </button>
        </div>
      ) : (
        <div className="grid gap-4">
          {notes.map((note) => {
            const isExpanded = isNoteExpanded(note.id);
            const shouldTruncate = note.content.length > 150;
            const displayContent = isExpanded || !shouldTruncate 
              ? note.content 
              : truncateContent(note.content);

            return (
              <div key={note.id} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <UserIcon className="h-4 w-4 text-gray-400" />
                      <span className="text-sm font-medium text-gray-900">
                        {note.creatorName || 'Unknown User'}
                      </span>
                      {note.taskId && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          🟦 Task Note
                        </span>
                      )}
                    </div>
                    
                    <div className="text-gray-700 mb-3 leading-relaxed">
                      {displayContent}
                      {shouldTruncate && (
                        <button
                          onClick={() => toggleNoteExpansion(note.id)}
                          className="ml-2 text-blue-600 hover:text-blue-800 font-medium"
                        >
                          {isExpanded ? 'Show less' : 'Show more'}
                        </button>
                      )}
                    </div>
                    
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      <div className="flex items-center space-x-1">
                        <CalendarIcon className="h-4 w-4" />
                        <span>{formatDate(note.createdAt)}</span>
                      </div>
                      {note.updatedAt !== note.createdAt && (
                        <div className="text-xs text-gray-400">
                          Updated {formatDate(note.updatedAt)}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      onClick={() => handleEditNote(note)}
                      className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Edit note"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteNote(note.id)}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete note"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Notes Form Modal */}
      {showForm && (
        <NotesForm
          note={editingNote}
          preSelectedLead={{
            id: parseInt(leadId),
            name: leadName,
            email: leadEmail,
            phone: leadPhone || ''
          }}
          onClose={() => {
            setShowForm(false);
            setEditingNote(null);
          }}
          onSuccess={handleFormSuccess}
        />
      )}
    </div>
  );
};

export default LeadNotesTab;
